const placementKey='webreader.controls-position';

// Placement belongs to this screen, independently of book/typography settings.
export function installFloatingControls(notify){
  const root=document.documentElement;
  let drag=null,settling=0,suppressClick=false;
  const pill=()=>document.querySelector('.topbar');
  const revealed=()=>root.classList.contains('header-revealed');
  const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));
  function bounds(element){
    const rect=element.getBoundingClientRect(),viewport=window.visualViewport;
    const left=viewport?.offsetLeft||0,top=viewport?.offsetTop||0;
    const width=viewport?.width||innerWidth,height=viewport?.height||innerHeight;
    const style=getComputedStyle(element),safe=side=>Math.max(8,parseFloat(style.getPropertyValue('--controls-safe-'+side))||0);
    let bottom=top+height-safe('bottom');
    const footer=document.querySelector('.reading-footer')?.getBoundingClientRect();
    // Reserve the reading bar's resting height throughout its reveal animation.
    const footerTop=footer?innerHeight-footer.height:innerHeight;
    if(footerTop>top&&footerTop<bottom)bottom=footerTop-8;
    const x=left+safe('left'),y=top+safe('top');
    return {left:x,right:Math.max(x,left+width-safe('right')-rect.width),top:y,bottom:Math.max(y,bottom-rect.height)};
  }
  function move(x,y){
    root.style.setProperty('--controls-left',x+'px');
    root.style.setProperty('--controls-top',y+'px');
    root.classList.add('controls-positioned');
  }
  function stopSettling(){clearTimeout(settling);settling=0;root.classList.remove('controls-settling');}
  function place(animate=false){
    const element=pill();if(!element||!revealed())return;
    stopSettling();
    notify('floatingBounds',{limits:bounds(element),animate});
  }
  function apply(value){
    stopSettling();
    if(!value){root.classList.remove('controls-positioned');return;}
    const element=pill();if(!element||!revealed())return;
    if(value.animate&&!root.classList.contains('reduced-motion')&&!matchMedia('(prefers-reduced-motion:reduce)').matches){element.getBoundingClientRect();root.classList.add('controls-settling');settling=setTimeout(stopSettling,300);}
    move(value.x,value.y);
    if(value.placement)try{localStorage.setItem(placementKey,JSON.stringify(value.placement));}catch{}
  }

  function sample(event){
    const now=performance.now();
    drag.samples.push({x:event.clientX,y:event.clientY,time:now});
    while(drag.samples.length>2&&drag.samples[1].time<now-100)drag.samples.shift();
  }
  function finish(event,cancelled=false){
    if(!drag||event&&event.pointerId!==drag.id)return;
    const active=drag;
    if(active.moved){
      suppressClick=true;
      if(!cancelled){
        sample(event);
        const rect=active.element.getBoundingClientRect();
        notify('dockFloating',{limits:bounds(active.element),left:rect.left,top:rect.top,samples:active.samples});
      }
    }
    drag=null;
    root.classList.remove('controls-interacting','controls-dragging');
    if(active.element.hasPointerCapture(active.id))active.element.releasePointerCapture(active.id);
    if(active.moved)place(!cancelled);
    // A touch browser may omit its compatibility click after captured dragging.
    // Handle a fresh stationary press here and consume any later native click.
    if(!cancelled&&!active.moved&&event?.pointerType==='touch'&&active.button){
      const rect=active.button.getBoundingClientRect();
      if(event.clientX>=rect.left&&event.clientX<=rect.right&&event.clientY>=rect.top&&event.clientY<=rect.bottom){
        suppressClick=true;event.preventDefault();active.button.click();
      }
    }
  }
  document.addEventListener('pointerdown',event=>{
    if(drag){if(event.pointerId!==drag.id)finish(null,true);return;}
    suppressClick=false;
    const element=event.target.closest?.('.topbar');
    if(!element||!revealed()||!event.isPrimary||event.button!==0||document.querySelector('.panel-overlay'))return;
    const rect=element.getBoundingClientRect(),wasSettling=!!settling;
    stopSettling();
    if(wasSettling)move(rect.left,rect.top);
    drag={id:event.pointerId,element,button:event.target.closest?.('button'),x:event.clientX,y:event.clientY,left:rect.left,top:rect.top,moved:false,samples:[]};
    root.classList.add('controls-interacting');
    sample(event);
  },true);
  document.addEventListener('pointermove',event=>{
    if(!drag||event.pointerId!==drag.id)return;
    const dx=event.clientX-drag.x,dy=event.clientY-drag.y;
    sample(event);
    if(!drag.moved&&Math.hypot(dx,dy)<8)return;
    if(!drag.moved){
      drag.moved=true;
      drag.element.setPointerCapture(drag.id);
      root.classList.add('controls-dragging');
    }
    event.preventDefault();
    const limits=bounds(drag.element);
    move(clamp(drag.left+dx,limits.left,limits.right),clamp(drag.top+dy,limits.top,limits.bottom));
  },{capture:true,passive:false});
  document.addEventListener('pointerup',event=>finish(event),true);
  document.addEventListener('pointercancel',event=>finish(event,true),true);
  // Touch initially captures the pressed button. Transferring capture to the
  // pill loses that original capture without cancelling the drag itself.
  document.addEventListener('lostpointercapture',event=>{if(event.target===drag?.element)finish(event,true);},true);
  document.addEventListener('click',event=>{
    if(suppressClick&&event.detail>0&&event.target.closest?.('.topbar')){
      suppressClick=false;event.preventDefault();event.stopImmediatePropagation();
    }
  },true);
  document.addEventListener('keydown',event=>{if(event.key==='Escape'&&drag){event.preventDefault();finish(null,true);}},true);
  window.addEventListener('blur',()=>finish(null,true));
  const resize=()=>{finish(null,true);place();};
  window.addEventListener('resize',resize);
  window.visualViewport?.addEventListener('resize',resize);
  window.visualViewport?.addEventListener('scroll',()=>{if(!drag)place();});
  const footerObserver=new ResizeObserver(()=>{if(!drag&&!settling)place();});
  let observedFooter=null;
  let visible=revealed();
  new MutationObserver(()=>{
    const next=revealed();if(next===visible)return;
    visible=next;
    if(next){
      const footer=document.querySelector('.reading-footer');
      if(footer&&footer!==observedFooter){footerObserver.disconnect();footerObserver.observe(footer);observedFooter=footer;}
      place();
    }else finish(null,true);
  }).observe(root,{attributes:true,attributeFilter:['class']});
  if(visible)place();
  return {apply};
}
